import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import { AutoSizer, Column, Table } from 'react-virtualized';
import Checkbox from '@mui/material/Checkbox';


const classes = {
  flexContainer: 'ReactVirtualizedDemo-flexContainer',
  tableRow: 'ReactVirtualizedDemo-tableRow',
  tableRowHover: 'ReactVirtualizedDemo-tableRowHover',
  tableCell: 'ReactVirtualizedDemo-tableCell',
  noClick: 'ReactVirtualizedDemo-noClick',
};

const styles = ({ theme }) => ({
  // temporary right-to-left patch, waiting for
  // https://github.com/bvaughn/react-virtualized/issues/454
  '& .ReactVirtualized__Table__headerRow': {
    ...(theme.direction === 'rtl' && {
      paddingLeft: '0 !important',
    }),
    ...(theme.direction !== 'rtl' && {
      paddingRight: undefined,
    }),
  },
  [`& .${classes.flexContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  [`& .${classes.tableRow}`]: {
    cursor: 'pointer',
  },
  [`& .${classes.tableRowHover}`]: {
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  [`& .${classes.tableCell}`]: {
    flex: 1,
  },
  [`& .${classes.noClick}`]: {
    cursor: 'initial',
  },
});

class MuiVirtualizedTable extends React.PureComponent {
  static defaultProps = {
    headerHeight: 48,
    rowHeight: 48,
  };

  getRowClassName = ({ index }) => {
    const { onRowClick } = this.props;

    return clsx(classes.tableRow, classes.flexContainer, {
      [classes.tableRowHover]: index !== -1 && onRowClick != null,
    });
  };

  cellRenderer = ({ cellData, columnIndex }) => {
    const { columns, rowHeight, onRowClick } = this.props;
    return (
      <TableCell
        component="div"
        className={clsx(classes.tableCell, classes.flexContainer, {
          [classes.noClick]: onRowClick == null,
        })}
        variant="body"
        style={{ height: rowHeight }}
        align={
          (columnIndex != null && columns[columnIndex].numeric) || false
            ? 'right'
            : 'left'
        }
      >
        {cellData}
      </TableCell>
    );
  };

  headerRenderer = ({ label, columnIndex }) => {
    const { headerHeight, columns } = this.props;

    return (
      <TableCell
        component="div"
        className={clsx(classes.tableCell, classes.flexContainer, classes.noClick)}
        variant="head"
        style={{ height: headerHeight }}
        align={columns[columnIndex].numeric || false ? 'right' : 'left'}
      >
        <span>{label}</span>
      </TableCell>
    );
  };

  onChangeHeaderCheckbox = event => {
    const { rows, setCheckedRows } = this.props;
    event.target.checked ? setCheckedRows(rows.map(item => item.id)) : setCheckedRows([]);
  };

  onChangeRowCheckbox = event => {
    const index = event.target.value;
    const { checkedRows, rows, setCheckedRows } = this.props;
    const newRow = rows[index].id;
    checkedRows.includes(newRow)
      ? setCheckedRows(old => old.filter(row => row !== newRow))
      : setCheckedRows(old => [...old, newRow]);
  };

  render() {
    const { columns, rowHeight, headerHeight, rows, checkedRows, ...tableProps } = this.props;

    return (
      <AutoSizer>
        {({ height, width }) => (
          <Table
            height={height}
            width={width}
            rowHeight={rowHeight}
            gridStyle={{
              direction: 'inherit',
            }}
            headerHeight={headerHeight}
            {...tableProps}
            rowClassName={this.getRowClassName}
          > 
            {columns.map(({ dataKey, ...other }, index) => {
                if (dataKey === "checkbox") {
                  return (
                      <Column
                          disableSort
                          dataKey="checkbox"
                          width={50}
                          headerRenderer={() => (
                              <Checkbox 
                                  indeterminate={
                                    checkedRows.length > 0 && checkedRows.length < rows.length
                                  }
                                  checked={checkedRows.length === rows.length}
                                  onChange={(event) => this.onChangeHeaderCheckbox(event)}
                              />
                          )}
                          cellRenderer={({ rowIndex }) => (
                              <Checkbox
                                  checked={checkedRows.includes(rows[rowIndex].id) === true}
                                  onChange={(event) => this.onChangeRowCheckbox(event)}
                                  value={rowIndex}
                              />
                          )}
                      /> 
                  );
                } else {
                    return (
                      <Column
                          key={dataKey}
                          headerRenderer={(headerProps) =>
                            this.headerRenderer({
                              ...headerProps,
                              columnIndex: index,
                            })
                          }
                          className={classes.flexContainer}
                          cellRenderer={this.cellRenderer}
                          dataKey={dataKey}
                          {...other}
                      />
                    );
                }
            })}
          </Table>
        )}
      </AutoSizer>
    );
  }
}

MuiVirtualizedTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      numeric: PropTypes.bool,
      width: PropTypes.number.isRequired,
    }),
  ).isRequired,
  headerHeight: PropTypes.number,
  onRowClick: PropTypes.func,
  rowHeight: PropTypes.number,
};

const VirtualizedTable = styled(MuiVirtualizedTable)(styles);

export default VirtualizedTable;
